import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	invalidateZoomConnectionQuery,
	useCallbackAuthorizeUrlMutation,
	useZoomConnectionQuery,
	useZoomDisconnectMutation,
} from '../../../../queries';
import { Button } from '../../../components-v2/Button';
import {
	Dialog,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import { LoadingSpinner } from '../../LoadingSpinner';
import { CancelIconFill } from '../../svgs/icons/CancelIconFill';
import { SuccessCheckIcon } from '../../svgs/icons/SuccessCheckIcon';
import { XIcon } from '../../svgs/icons/XIcon';
import { ZOOM_CONNECT_RETURN_PATH_KEY, ZOOM_SCHEDULE_BANNER_DISMISSED_KEY } from './constants';
import { styleSheet } from './styles';

interface IProps {
	/**
	 * Impersonation context to forward to the connection status query and the disconnect mutation, so an admin
	 * impersonating a user operates on that user's Zoom connection rather than the admin's own. Omit for the normal
	 * self-service case.
	 */
	impersonationContext?: Api.IImpersonationContext;
	/**
	 * In-app path to return to after the OAuth round-trip, so the user lands back on the surface they connected from.
	 * Must resolve to a route where a ZoomConnection is mounted to handle the callback outcome — e.g.
	 * `/settings/personal/zoom` auto-expands the Settings group so this component mounts. Defaults to `/schedule-meeting`.
	 */
	connectReturnPath?: string;
	/**
	 * Whether to render the "Zoom" heading and feature description. True for standalone use (e.g. the Schedule Meeting
	 * page). Set false when rendered inside a container that already labels/describes Zoom (e.g. the Personal Settings
	 * group header) to avoid duplication.
	 */
	showHeading?: boolean;
	/**
	 * When true, render nothing if Zoom is already connected. Used on the Schedule Meeting page to reclaim space once
	 * connected (the user can still disconnect from Personal Settings).
	 */
	hideWhenConnected?: boolean;
	/**
	 * When true, show a dismiss control that hides the banner for good (persisted in localStorage). Used for the
	 * Schedule Meeting nudge; the connection remains managed from Personal Settings.
	 */
	dismissible?: boolean;
	/**
	 * Extra Aphrodite styles for the outer container (e.g. surface-specific spacing). Applied to the rendered banner
	 * only, so no space is reserved when the component renders nothing.
	 */
	containerStyles?: StyleDeclarationValue[];
}

/**
 * Shared connect / disconnect UI for the per-user Zoom Scheduler integration. Renders a connection-status indicator
 * with a Connect or Disconnect action. Used in both Personal Settings and the Schedule Meeting home page.
 *
 * After the OAuth consent flow, the backend redirects (via AppLinks) to `connectReturnPath` with
 * `{ command: 'configCallback', outcome }` in route state. This component reads that state to toast success/failure and
 * refetch the connection status.
 */
export function ZoomConnection({
	impersonationContext,
	connectReturnPath = '/schedule-meeting',
	showHeading = true,
	hideWhenConnected = false,
	dismissible = false,
	containerStyles = [],
}: IProps = {}) {
	const history = useHistory();
	const userSession = useUserSession();
	const location = useLocation<{ command?: string; outcome?: string } | undefined>();
	const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = React.useState(false);
	// Scope the dismiss flag to the account (or session user) so dismiss state doesn't leak across impersonated
	// accounts in the same browser — per the impersonation rules in web/CLAUDE.md.
	const dismissKey = `${ZOOM_SCHEDULE_BANNER_DISMISSED_KEY}:${impersonationContext?.account?.id ?? userSession.user?.id ?? ''}`;
	const [isDismissed, setIsDismissed] = React.useState(false);
	// Re-read on mount and whenever the scoped key changes (e.g. the admin switches impersonated accounts while the
	// component stays mounted), so the flag never goes stale.
	React.useEffect(() => {
		setIsDismissed(dismissible && localStorage.getItem(dismissKey) === 'true');
	}, [dismissible, dismissKey]);

	const connectionQuery = useZoomConnectionQuery({
		impersonationContext,
		meta: { toastErrorMessage: 'Failed to load your Zoom connection status' },
	});

	const authorizeUrlMutation = useCallbackAuthorizeUrlMutation({
		onError: error => {
			// The return path was stashed before calling mutate; clear it so a failed start doesn't leave a stale path
			// for a later connect from a different surface.
			sessionStorage.removeItem(ZOOM_CONNECT_RETURN_PATH_KEY);
			toast.error(error.systemMessage ?? 'Unable to start Zoom connect. Please try again.');
		},
		onSuccess: url => window.location.replace(url),
	});

	const disconnectMutation = useZoomDisconnectMutation({
		onError: error => toast.error(error.systemMessage ?? 'Failed to disconnect Zoom'),
		onSuccess: () => {
			toast.success('Zoom disconnected');
			setIsDisconnectDialogOpen(false);
		},
	});

	// Handle the post-OAuth callback outcome carried in route state by AppLinks. Clear it after handling so it does
	// not re-fire on subsequent navigation/re-render.
	React.useEffect(() => {
		const state = location.state;
		if (state?.command !== 'configCallback' || !state.outcome) {
			return;
		}
		if (state.outcome === 'success') {
			toast.success('Zoom connected');
			invalidateZoomConnectionQuery();
		} else {
			// `outcome` originates from the OAuth callback URL query string, so never render it directly — a crafted
			// link could surface arbitrary attacker-controlled text in the toast (content-spoofing / phishing). Show a
			// fixed, generic failure message instead.
			toast.error('Unable to connect Zoom. Please try again.');
		}
		history.replace({ ...location, state: undefined });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.state]);

	const onConnect = () => {
		// Remember where to come back to before we leave the app for the Zoom consent screen.
		sessionStorage.setItem(ZOOM_CONNECT_RETURN_PATH_KEY, connectReturnPath);
		authorizeUrlMutation.mutate({ provider: Api.IntegrationProvider.Zoom });
	};
	const onDisconnect = () => disconnectMutation.mutate({ impersonationContext });
	const onDismiss = () => {
		localStorage.setItem(dismissKey, 'true');
		setIsDismissed(true);
	};

	if (dismissible && isDismissed) {
		return null;
	}

	if (connectionQuery.isPending) {
		// Avoid a spinner flash on surfaces that hide once connected — wait until status resolves.
		return hideWhenConnected ? null : (
			<div className={css(styleSheet.loading)}>
				<LoadingSpinner type='small' />
			</div>
		);
	}

	const isConnected = !!connectionQuery.data?.connected;
	const email = connectionQuery.data?.email;
	// Three states: connected; previously connected but now disconnected (offer Reconnect); never connected (a more
	// welcoming Connect prompt). "status" is absent for never-connected — see IZoomConnectionStatus.
	const isDisconnected = !isConnected && connectionQuery.data?.status === Api.ZoomConnectionState.Disconnected;

	if (hideWhenConnected && isConnected) {
		return null;
	}

	return (
		<div className={css(styleSheet.container, ...containerStyles)}>
			<div className={css(styleSheet.info)}>
				{showHeading && <span className={css(styleSheet.title)}>Zoom</span>}
				{isConnected && (
					<div className={css(styleSheet.statusRow)}>
						<SuccessCheckIcon />
						<span className={css(styleSheet.statusText)}>Connected{email ? ` as ${email}` : ''}</span>
					</div>
				)}
				{isDisconnected && (
					<div className={css(styleSheet.statusRow)}>
						<CancelIconFill />
						<span className={css(styleSheet.description)}>
							Your Zoom account{email ? ` (${email})` : ''} was disconnected. Reconnect to keep generating unique Zoom
							links.
						</span>
					</div>
				)}
				{!isConnected && !isDisconnected && (
					<span className={css(styleSheet.description)}>
						Connect your Zoom account to generate unique Zoom links for your meetings.
					</span>
				)}
			</div>

			<div className={css(styleSheet.actions)}>
				{isConnected ? (
					<Button
						variant='destructive'
						size='sm'
						data-testid='zoom-disconnect-button'
						onClick={() => setIsDisconnectDialogOpen(true)}
					>
						Disconnect
					</Button>
				) : (
					<Button
						variant='primary'
						size='sm'
						data-testid='zoom-connect-button'
						isLoading={authorizeUrlMutation.isPending}
						onClick={onConnect}
					>
						{isDisconnected ? 'Reconnect Zoom' : 'Connect Zoom'}
					</Button>
				)}
				{dismissible && (
					<Button
						variant='ghost'
						size='icon'
						styles={[styleSheet.dismissButton]}
						aria-label='Hide Zoom message'
						data-testid='zoom-dismiss-button'
						onClick={onDismiss}
					>
						<XIcon width={12} height={12} />
					</Button>
				)}
			</div>

			<Dialog open={isDisconnectDialogOpen} onOpenChange={setIsDisconnectDialogOpen}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Disconnect Zoom?</DialogTitle>
						<DialogDescription>
							Meeting types set to generate a unique Zoom link will no longer be able to create Zoom meetings until you
							reconnect.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant='secondary'
							disabled={disconnectMutation.isPending}
							onClick={() => setIsDisconnectDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button
							variant='destructive'
							data-testid='zoom-disconnect-confirm-button'
							isLoading={disconnectMutation.isPending}
							onClick={onDisconnect}
						>
							Disconnect
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</div>
	);
}
